import { localizedNamedLocation } from '@/helpers/localePath'

export const PREVIEW_BRIDGE_KEY = 'journey_preview_bridge'
export const PREVIEW_BRIDGE_TTL_MS = 60 * 1000

const DISPATCH_BY_TYPE = {
  unit: 'AppData/setPrevUnit',
  section: 'AppData/setPrevSection',
  week: 'AppData/setPrevWeek',
  mission: 'AppData/setPrevMission',
}

const FETCH_BY_TYPE = {
  unit: 'AppData/fetchPrevUnitById',
  section: 'AppData/fetchPrevSectionById',
  week: 'AppData/fetchPrevWeekById',
  mission: 'AppData/fetchPrevMissionById',
}

const STORE_ID_BY_TYPE = {
  unit: 'prevUnitId',
  section: 'prevSectionId',
}

function isDraftQuery(query) {
  return query?.draft === '1' || query?.draft === 'true'
}

export function writePreviewDraft({ type, lang, payload }) {
  const record = {
    type,
    lang: lang || 'en',
    payload,
    ts: Date.now(),
  }
  try {
    window.localStorage.setItem(PREVIEW_BRIDGE_KEY, JSON.stringify(record))
    return true
  } catch (err) {
    console.warn('[previewBridge] draft write skipped:', err?.name || err)
    return false
  }
}

export function readPreviewDraft() {
  if (typeof window === 'undefined' || !window.localStorage) return null
  try {
    const raw = window.localStorage.getItem(PREVIEW_BRIDGE_KEY)
    window.localStorage.removeItem(PREVIEW_BRIDGE_KEY)
    if (!raw) return null
    const record = JSON.parse(raw)
    if (!record?.ts || Date.now() - record.ts > PREVIEW_BRIDGE_TTL_MS) {
      return null
    }
    return record
  } catch (err) {
    console.warn('[previewBridge] draft read failed:', err?.name || err)
    return null
  }
}

/**
 * Open an admin preview in a new tab.
 * Saved items: pass id + lang and the new tab fetches from Firestore.
 * Unsaved builder state: pass payload (one-shot localStorage, not Vuex persist).
 */
export function openPreview(router, route, { name, id, lang, payload } = {}) {
  if (!router) return false

  const query = {}
  if (lang) query.lang = String(lang).toLowerCase()

  if (payload) {
    const wrote = writePreviewDraft({ type: name, lang, payload })
    if (wrote) {
      query.draft = '1'
    } else if (id) {
      query.id = id
    } else {
      return false
    }
  } else if (id) {
    query.id = id
  } else {
    return false
  }

  const location = {
    ...localizedNamedLocation(name, route || {}),
    query,
  }
  const href = router.resolve(location).href
  window.open(href, '_blank')
  return true
}

export async function hydratePreviewFromRoute({ route, store, type }) {
  if (!store || !type) return false

  const query = route?.query || {}
  const lang = String(query.lang || 'en').toLowerCase()
  const id = query.id
  const dispatchName = DISPATCH_BY_TYPE[type]
  const fetchName = FETCH_BY_TYPE[type]

  if (isDraftQuery(query)) {
    const record = readPreviewDraft()
    if (record?.payload && dispatchName) {
      await store.dispatch(dispatchName, record.payload)
      return true
    }
  }

  if (id && fetchName) {
    await store.dispatch(fetchName, { id, lang })
    return true
  }

  const fallbackIdKey = STORE_ID_BY_TYPE[type]
  const fallbackId = fallbackIdKey ? store.state.AppData?.[fallbackIdKey] : null
  if (fallbackId && fetchName) {
    await store.dispatch(fetchName, { id: fallbackId, lang })
    return true
  }

  return false
}
